/**
 * PharmacyPage — Aba unica de gestao Farmacia:
 *   - Receituario (Prescriptions): CRUD com upload de foto de receita
 *   - CMED: import CSV + busca por EAN + auditoria de produtos acima do PMC
 *   - SNGPC: visualizacao de movimentos de controlados + saldo + exportacao XML mensal
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText, Upload, Search, Pill, AlertTriangle, FileDown, Trash2, Plus, X, Save,
  Camera, BookOpen, Receipt, ScrollText, Beaker, ShieldCheck, Activity,
} from 'lucide-react';
import { localDateISO } from '../lib/dateUtils';
import { confirmDialog } from '../components/ConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PHARMA = `${BACKEND_URL}/api/pharmacy`;
const ERP = `${BACKEND_URL}/api/erp`;
const inputClass = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition";

const fmtBRL = (n) => `R$ ${(Number(n) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PharmacyPage = ({ companyId }) => {
  const [tab, setTab] = useState('prescriptions');

  return (
    <div className="space-y-4" data-testid="pharmacy-page">
      <div className="flex items-center gap-2">
        <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center"><Pill className="text-emerald-700" size={20} /></div>
        <div>
          <h2 className="text-2xl font-black text-slate-900">Farmacia</h2>
          <p className="text-xs text-slate-500">Receituario, CMED e SNGPC (Anvisa). Modulo regulamentado pela Portaria 344/98.</p>
        </div>
      </div>

      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        {[
          { k: 'prescriptions', icon: ScrollText, label: 'Receituario' },
          { k: 'cmed', icon: Beaker, label: 'CMED (Precos)' },
          { k: 'sngpc', icon: ShieldCheck, label: 'SNGPC (Controlados)' },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-xs font-bold uppercase whitespace-nowrap border-b-2 flex items-center gap-1.5 ${tab === t.k ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid={`pharmacy-tab-${t.k}`}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'prescriptions' && <PrescriptionsTab />}
      {tab === 'cmed' && <CMEDTab />}
      {tab === 'sngpc' && <SNGPCTab companyId={companyId} />}
    </div>
  );
};

/* ============ RECEITUARIO ============ */
const PrescriptionsTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    customer_name: '', customer_cpf: '', prescriber_name: '', prescriber_crm: '',
    prescription_date: localDateISO(), notes: '',
    notification_number: '', retained: false, image_b64: '', image_mime: 'image/jpeg',
  });
  const [submitting, setSubmitting] = useState(false);
  const upd = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${PHARMA}/prescriptions`, { withCredentials: true }); setRows(data.data || []); }
    catch { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error('Imagem max 3MB'); return; }
    const reader = new FileReader();
    reader.onloadend = () => upd('image_b64', reader.result?.split(',')[1] || '');
    upd('image_mime', f.type);
    reader.readAsDataURL(f);
  };

  const save = async () => {
    if (!form.customer_name || !form.prescriber_name || !form.prescriber_crm) {
      toast.error('Preencha cliente, medico e CRM');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${PHARMA}/prescriptions`, form, { withCredentials: true });
      toast.success('Receita cadastrada');
      setShowForm(false);
      setForm({ customer_name: '', customer_cpf: '', prescriber_name: '', prescriber_crm: '',
        prescription_date: localDateISO(), notes: '',
        notification_number: '', retained: false, image_b64: '', image_mime: 'image/jpeg' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setSubmitting(false); }
  };

  const remove = async (id) => {
    if (!(await confirmDialog({ title: 'Excluir esta receita?', confirmLabel: 'Confirmar', danger: true }))) return;
    try { await axios.delete(`${PHARMA}/prescriptions/${id}`, { withCredentials: true }); toast.success('Excluida'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };

  return (
    <div className="space-y-3" data-testid="prescriptions-tab">
      <div className="flex justify-between">
        <p className="text-xs text-slate-500">Receitas medicas registradas para venda de controlados/tarjados.</p>
        <button onClick={() => setShowForm(true)} className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg flex items-center gap-1" data-testid="prescription-new"><Plus size={12} /> Nova Receita</button>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        {loading ? <div className="p-10 text-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-600 border-t-transparent mx-auto" /></div>
        : rows.length === 0 ? (
          <div className="p-10 text-center" data-testid="prescriptions-empty">
            <ScrollText size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-500">Nenhuma receita cadastrada.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Cliente</th>
                <th className="text-left px-3 py-2">CPF</th>
                <th className="text-left px-3 py-2">Medico</th>
                <th className="text-left px-3 py-2">CRM</th>
                <th className="text-left px-3 py-2">Data</th>
                <th className="text-center px-3 py-2">Retida</th>
                <th className="text-center px-3 py-2">Foto</th>
                <th className="text-right px-3 py-2">Acoes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(r => (
                <tr key={r.id} className="hover:bg-emerald-50/40" data-testid={`prescription-row-${r.id}`}>
                  <td className="px-3 py-2 text-xs font-bold text-slate-800">{r.customer_name}</td>
                  <td className="px-3 py-2 text-xs font-mono">{r.customer_cpf || '—'}</td>
                  <td className="px-3 py-2 text-xs">{r.prescriber_name}</td>
                  <td className="px-3 py-2 text-xs font-mono">{r.prescriber_crm}</td>
                  <td className="px-3 py-2 text-xs">{r.prescription_date || '—'}</td>
                  <td className="px-3 py-2 text-center text-xs">{r.retained ? '✓' : '—'}</td>
                  <td className="px-3 py-2 text-center text-xs">{r.image_b64 ? '📷' : '—'}</td>
                  <td className="px-3 py-2 text-right"><button onClick={() => remove(r.id)} className="text-xs font-bold text-rose-600 hover:underline">Excluir</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center justify-center p-0 md:p-4" data-testid="prescription-modal">
          <div className="bg-white w-full md:max-w-2xl md:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-5 py-4 text-white flex items-center justify-between">
              <h3 className="font-black">Nova Receita Medica</h3>
              <button onClick={() => setShowForm(false)} className="p-2 hover:bg-white/20 rounded-lg"><X /></button>
            </div>
            <div className="overflow-y-auto p-5 space-y-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Cliente *</label><input value={form.customer_name} onChange={e => upd('customer_name', e.target.value)} className={inputClass} data-testid="prescription-customer-name" /></div>
              <div><label className="block text-xs font-bold text-slate-500 mb-1 uppercase">CPF</label><input value={form.customer_cpf} onChange={e => upd('customer_cpf', e.target.value)} className={inputClass} placeholder="000.000.000-00" data-testid="prescription-customer-cpf" /></div>
              <div><label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Medico *</label><input value={form.prescriber_name} onChange={e => upd('prescriber_name', e.target.value)} className={inputClass} data-testid="prescription-prescriber" /></div>
              <div><label className="block text-xs font-bold text-slate-500 mb-1 uppercase">CRM/UF *</label><input value={form.prescriber_crm} onChange={e => upd('prescriber_crm', e.target.value)} className={inputClass} placeholder="CRM/GO 12345" data-testid="prescription-crm" /></div>
              <div><label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Data Receita</label><input type="date" value={form.prescription_date} onChange={e => upd('prescription_date', e.target.value)} className={inputClass} /></div>
              <div><label className="block text-xs font-bold text-slate-500 mb-1 uppercase">N. Notificacao (tarja preta)</label><input value={form.notification_number} onChange={e => upd('notification_number', e.target.value)} className={inputClass} data-testid="prescription-notification" /></div>
              <label className="flex items-center gap-2 p-2 bg-rose-50 border border-rose-200 rounded-lg md:col-span-2 cursor-pointer">
                <input type="checkbox" checked={form.retained} onChange={e => upd('retained', e.target.checked)} className="w-4 h-4 accent-rose-600" data-testid="prescription-retained" />
                <span className="text-sm font-bold text-rose-900">Receita retida (farmacia ficou com a via)</span>
              </label>
              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Foto da Receita</label>
                <input type="file" accept="image/*" onChange={onFile} className="text-xs" data-testid="prescription-upload" />
                {form.image_b64 && <div className="mt-2 inline-flex items-center gap-1 text-[10px] text-emerald-600"><Camera size={12} /> Foto carregada ({Math.round(form.image_b64.length * 0.75 / 1024)}KB)</div>}
              </div>
              <div className="md:col-span-2"><label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Observacoes</label><textarea rows={2} value={form.notes} onChange={e => upd('notes', e.target.value)} className={inputClass} /></div>
            </div>
            <div className="border-t border-slate-200 bg-slate-50 px-5 py-3 flex justify-end gap-2">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 text-slate-600 text-sm font-bold">Cancelar</button>
              <button onClick={save} disabled={submitting} className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white rounded-xl font-bold text-sm flex items-center gap-2" data-testid="prescription-save"><Save size={14} /> {submitting ? 'Salvando...' : 'Salvar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ============ CMED ============ */
const CMEDTab = () => {
  const [results, setResults] = useState([]);
  const [audit, setAudit] = useState([]);
  const [search, setSearch] = useState('');
  const [uf, setUf] = useState('GO');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [subTab, setSubTab] = useState('search');

  const doSearch = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ uf });
      if (/^\d{8,13}$/.test(search.trim())) params.set('ean', search.trim());
      else if (search.trim()) params.set('q', search.trim());
      const { data } = await axios.get(`${PHARMA}/cmed/search?${params}`, { withCredentials: true });
      setResults(data.data || []);
    } catch (err) { toast.error('Erro: ' + (err.response?.data?.detail || err.message)); }
    finally { setLoading(false); }
  };

  const loadAudit = async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${PHARMA}/cmed/audit`, { withCredentials: true }); setAudit(data.data || []); }
    catch (err) { toast.error('Erro: ' + (err.response?.data?.detail || err.message)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (subTab === 'audit') loadAudit(); }, [subTab]);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('uf', uf);
      const { data } = await axios.post(`${PHARMA}/cmed/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Importado: +${data.inserted} | atualizados ${data.updated} | pulados ${data.skipped}`);
      if (data.errors?.length) toast.warning(`${data.errors.length} erros (ver logs)`);
    } catch (err) { toast.error('Erro: ' + (err.response?.data?.detail || err.message)); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const clearAll = async () => {
    if (!(await confirmDialog({ title: 'Limpar TODA a tabela CMED do tenant? Esta acao nao pode ser desfeita.', confirmLabel: 'Confirmar', danger: true }))) return;
    try { const { data } = await axios.delete(`${PHARMA}/cmed/all`, { withCredentials: true }); toast.success(`Deletados ${data.deleted} precos`); setResults([]); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };

  return (
    <div className="space-y-3" data-testid="cmed-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1">
          {[{ k: 'search', l: 'Buscar' }, { k: 'audit', l: 'Auditoria' }].map(t => (
            <button key={t.k} onClick={() => setSubTab(t.k)} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${subTab === t.k ? 'bg-emerald-600 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-testid={`cmed-subtab-${t.k}`}>{t.l}</button>
          ))}
        </div>
        <div className="flex-1 min-w-[200px]" />
        <select value={uf} onChange={e => setUf(e.target.value)} className={`${inputClass} w-24`} data-testid="cmed-uf">
          {['GO','SP','RJ','MG','RS','PR','SC','BA','PE','CE','DF','MT','MS','ES','PA','MA','PB','RN','AL','SE','PI','AC','RO','AM','RR','AP','TO'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <label className="px-3 py-2 bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-bold rounded-lg flex items-center gap-1 cursor-pointer" data-testid="cmed-upload-label">
          <Upload size={12} /> {uploading ? 'Importando...' : 'Importar CSV'}
          <input type="file" accept=".csv" onChange={onUpload} className="hidden" disabled={uploading} data-testid="cmed-upload" />
        </label>
        <button onClick={clearAll} className="px-3 py-2 bg-slate-200 hover:bg-rose-200 text-slate-700 text-xs font-bold rounded-lg flex items-center gap-1" data-testid="cmed-clear"><Trash2 size={12} /> Limpar</button>
      </div>

      {subTab === 'search' && (
        <>
          <div className="bg-emerald-50 border-l-4 border-emerald-500 px-3 py-2 text-[11px] text-emerald-900 rounded-r">
            <strong>CMED — Camara de Regulacao do Mercado de Medicamentos.</strong> Importe o tabelao oficial publicado pela Anvisa (CSV com colunas EAN, principio_ativo, laboratorio, apresentacao, tipo, pmc, pf). Tenant atual: <strong>{uf}</strong>.
          </div>
          <div className="flex gap-2">
            <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && doSearch()} placeholder="EAN13 ou principio ativo (ex: Paracetamol)" className={inputClass} data-testid="cmed-search-input" />
            <button onClick={doSearch} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg flex items-center gap-1" data-testid="cmed-search-btn"><Search size={12} /> Buscar</button>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            {results.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-400">Faca uma busca acima.</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600"><tr><th className="text-left px-3 py-2">EAN</th><th className="text-left px-3 py-2">Principio Ativo</th><th className="text-left px-3 py-2">Apresentacao</th><th className="text-left px-3 py-2">Lab.</th><th className="text-right px-3 py-2">PF</th><th className="text-right px-3 py-2">PMC</th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {results.map(r => (
                    <tr key={r.id} className="hover:bg-emerald-50/40" data-testid={`cmed-row-${r.id}`}>
                      <td className="px-3 py-2 font-mono">{r.ean}</td>
                      <td className="px-3 py-2 font-bold">{r.active_ingredient}</td>
                      <td className="px-3 py-2">{r.presentation}</td>
                      <td className="px-3 py-2">{r.manufacturer}</td>
                      <td className="px-3 py-2 text-right text-slate-600">{fmtBRL(r.pf)}</td>
                      <td className="px-3 py-2 text-right font-bold text-emerald-700">{fmtBRL(r.pmc)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {subTab === 'audit' && (
        <>
          <div className="bg-rose-50 border-l-4 border-rose-500 px-3 py-2 text-[11px] text-rose-900 rounded-r">
            <strong>Auditoria CMED.</strong> Produtos cujo <em>preco de venda atual</em> excede o PMC (Preco Maximo ao Consumidor) regulamentado. Vender acima do PMC e infracao sanitaria.
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            {loading ? <div className="p-8 text-center"><div className="animate-spin rounded-full h-6 w-6 border-2 border-emerald-600 border-t-transparent mx-auto" /></div>
            : audit.length === 0 ? (
              <div className="p-8 text-center" data-testid="cmed-audit-empty">
                <ShieldCheck size={48} className="mx-auto text-emerald-300 mb-3" />
                <p className="text-sm text-emerald-700 font-bold">Nenhum produto acima do PMC.</p>
                <p className="text-[11px] text-slate-400">Conformidade CMED OK.</p>
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-rose-50 text-rose-700"><tr><th className="text-left px-3 py-2">Produto</th><th className="text-left px-3 py-2">EAN</th><th className="text-right px-3 py-2">Preco Venda</th><th className="text-right px-3 py-2">PMC</th><th className="text-right px-3 py-2">Excesso</th><th className="text-right px-3 py-2">%</th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {audit.map(r => (
                    <tr key={r.product_id} className="hover:bg-rose-50/30" data-testid={`audit-row-${r.product_id}`}>
                      <td className="px-3 py-2"><p className="font-bold text-slate-800">{r.name}</p><p className="text-[10px] text-slate-400">{r.active_ingredient}</p></td>
                      <td className="px-3 py-2 font-mono">{r.barcode || '—'}</td>
                      <td className="px-3 py-2 text-right text-rose-700 font-bold">{fmtBRL(r.price)}</td>
                      <td className="px-3 py-2 text-right text-slate-600">{fmtBRL(r.pmc)}</td>
                      <td className="px-3 py-2 text-right text-rose-700 font-bold">+{fmtBRL(r.excess)}</td>
                      <td className="px-3 py-2 text-right text-rose-700 font-bold">+{r.excess_percent}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/* ============ SNGPC ============ */
const SNGPCTab = ({ companyId }) => {
  const [month, setMonth] = useState(localDateISO().slice(0, 7));
  const [movs, setMovs] = useState([]);
  const [balance, setBalance] = useState([]);
  const [transmissions, setTransmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState('movs');
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, t] = await Promise.all([
        axios.get(`${PHARMA}/sngpc/movimentos?month=${month}${companyId ? `&company_id=${companyId}` : ''}`, { withCredentials: true }),
        axios.get(`${PHARMA}/sngpc/transmissions`, { withCredentials: true }),
      ]);
      setMovs(m.data.data || []);
      setTransmissions(t.data.data || []);
      if (companyId) {
        const b = await axios.get(`${PHARMA}/sngpc/balance?company_id=${companyId}&month=${month}`, { withCredentials: true });
        setBalance(b.data.data || []);
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [month, companyId]);
  useEffect(() => { load(); }, [load]);

  const exportXML = async () => {
    if (!companyId) { toast.error('Selecione uma filial no topo'); return; }
    setGenerating(true);
    try {
      const res = await axios.post(`${PHARMA}/sngpc/export?month=${month}&company_id=${companyId}`, {}, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/xml' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `sngpc_${month}.xml`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`XML gerado: ${res.headers['x-movements-count']} movimentos`);
      load();
    } catch (err) { toast.error('Erro: ' + (err.response?.data?.detail || err.message)); }
    finally { setGenerating(false); }
  };

  const totals = useMemo(() => {
    const agg = { entradas: 0, saidas: 0, perdas: 0, devolucoes: 0, transferencias: 0 };
    movs.forEach(m => {
      const t = m.movement_type || '';
      if (t === 'entrada') agg.entradas += m.quantity;
      else if (t === 'saida') agg.saidas += m.quantity;
      else if (t === 'perda') agg.perdas += m.quantity;
      else if (t === 'devolucao') agg.devolucoes += m.quantity;
      else if (t === 'transferencia') agg.transferencias += m.quantity;
    });
    return agg;
  }, [movs]);

  return (
    <div className="space-y-3" data-testid="sngpc-tab">
      <div className="bg-rose-50 border-l-4 border-rose-500 px-3 py-2 text-[11px] text-rose-900 rounded-r">
        <strong>SNGPC — Sistema Nacional de Gerenciamento de Produtos Controlados.</strong> Registro mensal obrigatorio Anvisa para medicamentos das listas A1-F (Portaria 344/98). Os movimentos sao gravados automaticamente quando o produto controlado entra/sai do estoque.
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1">
          {[{ k: 'movs', l: 'Movimentos' }, { k: 'balance', l: 'Saldo' }, { k: 'history', l: 'Transmissoes' }].map(t => (
            <button key={t.k} onClick={() => setSubTab(t.k)} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${subTab === t.k ? 'bg-emerald-600 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-testid={`sngpc-subtab-${t.k}`}>{t.l}</button>
          ))}
        </div>
        <div className="flex-1 min-w-[200px]" />
        <input type="month" value={month} onChange={e => setMonth(e.target.value)} className={`${inputClass} w-40`} data-testid="sngpc-month" />
        <button onClick={exportXML} disabled={generating || !companyId} className="px-3 py-2 bg-rose-600 hover:bg-rose-700 disabled:bg-slate-300 text-white text-xs font-bold rounded-lg flex items-center gap-1" data-testid="sngpc-export" title={!companyId ? 'Selecione uma filial' : ''}>
          <FileDown size={12} /> {generating ? 'Gerando...' : 'Exportar XML'}
        </button>
      </div>

      {/* Cards de totais */}
      {subTab === 'movs' && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3"><p className="text-[10px] font-bold text-emerald-700 uppercase">Entradas</p><p className="text-xl font-black text-emerald-800">{totals.entradas}</p></div>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-3"><p className="text-[10px] font-bold text-rose-700 uppercase">Saidas</p><p className="text-xl font-black text-rose-800">{totals.saidas}</p></div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3"><p className="text-[10px] font-bold text-amber-700 uppercase">Perdas</p><p className="text-xl font-black text-amber-800">{totals.perdas}</p></div>
            <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-3"><p className="text-[10px] font-bold text-cyan-700 uppercase">Devolucoes</p><p className="text-xl font-black text-cyan-800">{totals.devolucoes}</p></div>
            <div className="bg-violet-50 border border-violet-200 rounded-xl p-3"><p className="text-[10px] font-bold text-violet-700 uppercase">Transferencias</p><p className="text-xl font-black text-violet-800">{totals.transferencias}</p></div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            {loading ? <div className="p-8 text-center"><div className="animate-spin rounded-full h-6 w-6 border-2 border-emerald-600 border-t-transparent mx-auto" /></div>
            : movs.length === 0 ? (
              <div className="p-8 text-center" data-testid="sngpc-movs-empty">
                <Activity size={48} className="mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-slate-500">Nenhum movimento de controlado em {month}.</p>
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600"><tr><th className="text-left px-3 py-2">Data</th><th className="text-left px-3 py-2">Tipo</th><th className="text-left px-3 py-2">Produto</th><th className="text-left px-3 py-2">Lista</th><th className="text-right px-3 py-2">Qtd</th><th className="text-left px-3 py-2">CPF/Cliente</th><th className="text-left px-3 py-2">Receita</th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {movs.map(r => (
                    <tr key={r.id} className="hover:bg-emerald-50/30" data-testid={`sngpc-mov-${r.id}`}>
                      <td className="px-3 py-2 text-[10px]">{r.created_at?.slice(0, 16).replace('T', ' ')}</td>
                      <td className="px-3 py-2"><span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${r.movement_type === 'entrada' ? 'bg-emerald-100 text-emerald-700' : r.movement_type === 'saida' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>{r.movement_type}</span></td>
                      <td className="px-3 py-2 font-bold">{r.product_name} <span className="text-[10px] text-slate-400 font-normal">({r.product_sku})</span></td>
                      <td className="px-3 py-2 font-mono text-rose-700">{r.controlled_list}</td>
                      <td className="px-3 py-2 text-right font-bold">{r.quantity}</td>
                      <td className="px-3 py-2 text-[10px]">{r.customer_name && (<div><strong>{r.customer_name}</strong><div className="font-mono text-slate-400">{r.customer_cpf}</div></div>)}</td>
                      <td className="px-3 py-2 text-[10px]">{r.prescriber_name && (<div><strong>{r.prescriber_name}</strong><div className="font-mono text-slate-400">{r.prescriber_crm}</div></div>)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {subTab === 'balance' && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          {!companyId ? <div className="p-8 text-center text-sm text-amber-700">Selecione uma filial no topo da pagina para calcular saldo.</div>
          : balance.length === 0 ? <div className="p-8 text-center text-sm text-slate-400">Nenhum saldo a exibir.</div>
          : (
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600"><tr><th className="text-left px-3 py-2">Produto</th><th className="text-left px-3 py-2">Lista</th><th className="text-right px-3 py-2">Entradas</th><th className="text-right px-3 py-2">Saidas</th><th className="text-right px-3 py-2">Perdas</th><th className="text-right px-3 py-2">Saldo SNGPC</th><th className="text-right px-3 py-2">Estoque Fisico</th><th className="text-right px-3 py-2">Divergencia</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {balance.map(r => (
                  <tr key={r.product_id} className={Math.abs(r.divergencia) > 0.01 ? 'bg-rose-50/40' : ''} data-testid={`sngpc-balance-${r.product_id}`}>
                    <td className="px-3 py-2 font-bold">{r.product_name}</td>
                    <td className="px-3 py-2 font-mono text-rose-700">{r.controlled_list}</td>
                    <td className="px-3 py-2 text-right text-emerald-700">{r.entradas}</td>
                    <td className="px-3 py-2 text-right text-rose-700">{r.saidas}</td>
                    <td className="px-3 py-2 text-right">{r.perdas}</td>
                    <td className="px-3 py-2 text-right font-bold">{r.saldo_sngpc}</td>
                    <td className="px-3 py-2 text-right text-cyan-700">{r.estoque_fisico}</td>
                    <td className={`px-3 py-2 text-right font-bold ${Math.abs(r.divergencia) > 0.01 ? 'text-rose-700' : 'text-emerald-700'}`}>{r.divergencia > 0 ? '+' : ''}{r.divergencia}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {subTab === 'history' && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          {transmissions.length === 0 ? <div className="p-8 text-center text-sm text-slate-400" data-testid="sngpc-history-empty">Nenhuma transmissao gerada ainda.</div>
          : (
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600"><tr><th className="text-left px-3 py-2">Data</th><th className="text-left px-3 py-2">Mes Ref.</th><th className="text-right px-3 py-2">Movimentos</th><th className="text-right px-3 py-2">XML (bytes)</th><th className="text-center px-3 py-2">Status</th><th className="text-left px-3 py-2">Gerado por</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {transmissions.map(t => (
                  <tr key={t.id} data-testid={`sngpc-transmission-${t.id}`}>
                    <td className="px-3 py-2">{t.generated_at?.slice(0, 16).replace('T', ' ')}</td>
                    <td className="px-3 py-2 font-mono">{t.month_ref}</td>
                    <td className="px-3 py-2 text-right">{t.total_movements}</td>
                    <td className="px-3 py-2 text-right text-slate-400">{(t.xml_size_bytes || 0).toLocaleString('pt-BR')}</td>
                    <td className="px-3 py-2 text-center"><span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold">{t.status}</span></td>
                    <td className="px-3 py-2">{t.generated_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default PharmacyPage;
